import React, { useState, useEffect } from 'react';
import { 
  Wallet as WalletIcon, 
  ArrowUpRight, 
  ArrowDownRight, 
  Download, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  CreditCard,
  Banknote,
  History,
  RefreshCw,
  Calendar,
  User,
  Shield,
  Zap,
  TrendingUp,
  DollarSign,
  Lock,
  Smartphone,
  Globe
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useSeller } from '../../context/SellerContext';

const Wallet = () => {
  const { wallet, requestPayout, refreshData } = useSeller();
  
  const balance = wallet?.availableBalance || 0;
  const transactions = wallet?.recentPayouts || [];

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('bank');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const stats = {
    totalWithdrawn: wallet?.withdrawn || 0,
    totalEarned: wallet?.totalEarnings || 0,
    pendingSettlement: wallet?.pendingClearance || 0,
    lastPayout: transactions.length > 0 ? transactions[0].createdAt : null
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }
    if (amount < 500) {
      toast.error('Minimum withdrawal amount is ₹500');
      return;
    }

    setLoading(true);
    const methodString = selectedMethod === 'bank' ? 'Bank Transfer' : selectedMethod === 'upi' ? 'UPI' : 'Mobile Wallet';
    const res = await requestPayout(amount, methodString);
    if (res && res.success) {
      setShowWithdrawModal(false);
      setWithdrawAmount('');
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed': return <CheckCircle size={14} />;
      case 'Pending': return <Clock size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return '#ef4444';
    switch(status.toLowerCase()) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'processing': return '#3b82f6';
      default: return '#ef4444';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor, trend }) => (
    <div className="wallet-stat-card">
      <div className="stat-icon-wrapper" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <p className="stat-label">{title}</p>
        <h3 className="stat-number">{typeof value === 'number' ? `₹${value.toLocaleString()}` : value}</h3>
        {trend && (
          <div className="stat-trend" style={{ color: trend > 0 ? '#10b981' : '#ef4444' }}>
            {trend > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(trend)}% from last month
          </div>
        )}
      </div>
    </div>
  );

  const QuickActionCard = ({ icon: Icon, title, subtitle, onClick, color }) => (
    <div className="quick-action-card" onClick={onClick}>
      <div className="action-icon" style={{ backgroundColor: `${color}15` }}>
        <Icon size={24} color={color} />
      </div>
      <div className="action-info">
        <h6 className="action-title">{title}</h6>
        <p className="action-subtitle">{subtitle}</p>
      </div>
      <ArrowUpRight size={16} className="action-arrow" />
    </div>
  );

  const recentTransactions = transactions.slice(0, 5);

  return (
    <div className="wallet-page">
      {/* Header */}
      <div className="wallet-header">
        <div className="header-left">
          <h1 className="page-title-wallet">Wallet & Payouts</h1>
          <p className="page-subtitle-wallet">Manage your earnings and withdrawal requests</p>
        </div>
        <button className="btn-refresh-wallet" onClick={refreshData}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Balance Card */}
      <div className="balance-hero-card">
        <div className="balance-content">
          <div className="balance-info">
            <span className="balance-label">
              <WalletIcon size={18} />
              Available Balance
            </span>
            <h1 className="balance-amount">₹{balance.toLocaleString()}</h1>
            <div className="balance-meta">
              <span className="meta-item">
                <CheckCircle size={12} />
                Ready to withdraw
              </span>
              <span className="meta-item">
                <Lock size={12} />
                Secure wallet
              </span>
            </div>
          </div>
          <button 
            className="withdraw-button"
            onClick={() => setShowWithdrawModal(true)}
            disabled={balance === 0}
          >
            <CreditCard size={18} />
            Request Withdrawal
          </button>
        </div>
        <div className="balance-decoration"></div>
      </div>

      {/* Stats Grid */}
      <div className="stats-wallet-grid">
        <StatCard 
          title="Total Earned" 
          value={stats.totalEarned} 
          icon={TrendingUp} 
          color="#10b981" 
          bgColor="#d1fae5"
          trend={12}
        />
        <StatCard 
          title="Total Withdrawn" 
          value={stats.totalWithdrawn} 
          icon={Banknote} 
          color="#3b82f6" 
          bgColor="#dbeafe"
          trend={8}
        />
        <StatCard 
          title="Pending Settlement" 
          value={stats.pendingSettlement} 
          icon={Clock} 
          color="#f59e0b" 
          bgColor="#fed7aa"
          trend={-5}
        />
        <StatCard 
          title="Last Payout" 
          value={stats.lastPayout ? formatDate(stats.lastPayout) : 'N/A'} 
          icon={Calendar} 
          color="#8b5cf6" 
          bgColor="#e9d5ff"
        />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-grid">
        <QuickActionCard 
          icon={Banknote} 
          title="Request Withdrawal" 
          subtitle="Transfer funds to your bank account"
          onClick={() => setShowWithdrawModal(true)}
          color="#10b981"
        />
        <QuickActionCard 
          icon={History} 
          title="Transaction History" 
          subtitle="View all your financial activities"
          onClick={() => document.getElementById('transactions-section').scrollIntoView({ behavior: 'smooth' })}
          color="#3b82f6"
        />
        <QuickActionCard 
          icon={Smartphone} 
          title="Payment Methods" 
          subtitle="Manage your payout options"
          onClick={() => toast.info('Payment methods coming soon')}
          color="#8b5cf6"
        />
        <QuickActionCard 
          icon={Shield} 
          title="Security Settings" 
          subtitle="Secure your wallet"
          onClick={() => toast.info('Security settings coming soon')}
          color="#ef4444"
        />
      </div>

      {/* Withdrawal Methods */}
      <div className="withdrawal-methods-card">
        <div className="section-header">
          <div>
            <h5 className="section-title">Withdrawal Methods</h5>
            <p className="section-subtitle">Choose your preferred payout option</p>
          </div>
        </div>
        <div className="methods-grid">
          <div className={`method-card ${selectedMethod === 'bank' ? 'selected' : ''}`} onClick={() => setSelectedMethod('bank')}>
            <div className="method-icon">
              <Banknote size={24} />
            </div>
            <div className="method-info">
              <h6>Bank Transfer</h6>
              <p>1-3 business days</p>
            </div>
            <div className="method-check">
              {selectedMethod === 'bank' && <CheckCircle size={20} color="#10b981" />}
            </div>
          </div>
          <div className={`method-card ${selectedMethod === 'upi' ? 'selected' : ''}`} onClick={() => setSelectedMethod('upi')}>
            <div className="method-icon">
              <Smartphone size={24} />
            </div>
            <div className="method-info">
              <h6>UPI Transfer</h6>
              <p>Instant payout</p>
            </div>
            <div className="method-check">
              {selectedMethod === 'upi' && <CheckCircle size={20} color="#10b981" />}
            </div>
          </div>
          <div className={`method-card ${selectedMethod === 'wallet' ? 'selected' : ''}`} onClick={() => setSelectedMethod('wallet')}>
            <div className="method-icon">
              <WalletIcon size={24} />
            </div>
            <div className="method-info">
              <h6>Mobile Wallet</h6>
              <p>Instant payout</p>
            </div>
            <div className="method-check">
              {selectedMethod === 'wallet' && <CheckCircle size={20} color="#10b981" />}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div id="transactions-section" className="transactions-history-card">
        <div className="section-header">
          <div>
            <h5 className="section-title">Transaction History</h5>
            <p className="section-subtitle">All your financial activities in one place</p>
          </div>
          <button className="btn-export-history">
            <Download size={16} />
            Export History
          </button>
        </div>

        <div className="transactions-list">
          {recentTransactions.map((tx) => (
            <div key={tx._id} className="transaction-item">
              <div className="transaction-icon" style={{ backgroundColor: '#fee2e2' }}>
                <ArrowDownRight size={20} color="#ef4444" />
              </div>
              <div className="transaction-details">
                <div className="transaction-header">
                  <span className="transaction-id">#{tx._id.substring(tx._id.length - 6).toUpperCase()}</span>
                  <span className="transaction-type">Payout</span>
                </div>
                <div className="transaction-meta">
                  <span className="meta-date">{formatDate(tx.createdAt)} at {formatTime(tx.createdAt)}</span>
                  {tx.method && <span className="meta-method">{tx.method}</span>}
                </div>
              </div>
              <div className="transaction-amount" style={{ color: '#ef4444' }}>
                -₹{tx.amount.toLocaleString()}
              </div>
              <div className="transaction-status" style={{ color: getStatusColor(tx.status) }}>
                {getStatusIcon(tx.status ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1) : 'Pending')}
                <span>{tx.status ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1) : 'Pending'}</span>
              </div>
            </div>
          ))}
        </div>

        {transactions.length > 5 && (
          <div className="view-all-wrapper">
            <button className="view-all-btn" onClick={() => toast.info('View all coming soon')}>
              View All Transactions
              <ArrowUpRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="modal-overlay-wallet" onClick={() => setShowWithdrawModal(false)}>
          <div className="modal-container-wallet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wallet">
              <h3>Request Withdrawal</h3>
              <button className="modal-close-wallet" onClick={() => setShowWithdrawModal(false)}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body-wallet">
              <div className="modal-info">
                <div className="info-row">
                  <span>Available Balance</span>
                  <strong>₹{balance.toLocaleString()}</strong>
                </div>
                <div className="info-row">
                  <span>Minimum Withdrawal</span>
                  <strong>₹500</strong>
                </div>
              </div>

              <div className="form-group">
                <label>Enter Amount (₹)</label>
                <div className="amount-input-wrapper">
                  <DollarSign size={18} />
                  <input 
                    type="number" 
                    className="amount-input"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Withdrawal Method</label>
                <div className="method-selector">
                  <button className={`method-option ${selectedMethod === 'bank' ? 'active' : ''}`} onClick={() => setSelectedMethod('bank')}>
                    <Banknote size={16} />
                    Bank Transfer
                  </button>
                  <button className={`method-option ${selectedMethod === 'upi' ? 'active' : ''}`} onClick={() => setSelectedMethod('upi')}>
                    <Smartphone size={16} />
                    UPI
                  </button>
                  <button className={`method-option ${selectedMethod === 'wallet' ? 'active' : ''}`} onClick={() => setSelectedMethod('wallet')}>
                    <WalletIcon size={16} />
                    Mobile Wallet
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowWithdrawModal(false)}>
                  Cancel
                </button>
                <button className="btn-submit" onClick={handleWithdraw} disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Confirm Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.wallet-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.wallet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}

.page-title-wallet {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-wallet {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.btn-refresh-wallet {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh-wallet:hover {
  border-color: #10b981;
  color: #10b981;
}

.balance-hero-card {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 28px;
  padding: 32px;
  margin-bottom: 32px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.25);
}

.balance-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
  flex-wrap: wrap;
  gap: 20px;
}

.balance-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.9);
  font-size: 14px;
  margin-bottom: 12px;
}

.balance-amount {
  font-size: 48px;
  font-weight: 800;
  color: white;
  margin: 0 0 16px;
}

.balance-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255,255,255,0.8);
  font-size: 12px;
}

.withdraw-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: white;
  border: none;
  border-radius: 60px;
  font-size: 16px;
  font-weight: 600;
  color: #059669;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.withdraw-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.withdraw-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.balance-decoration {
  position: absolute;
  top: -50%;
  right: -20%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  pointer-events: none;
}

.stats-wallet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.wallet-stat-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.wallet-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.stat-icon-wrapper {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 4px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-top: 6px;
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.quick-action-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
  position: relative;
}

.quick-action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.action-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.action-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.action-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.action-arrow {
  margin-left: auto;
  color: #94a3b8;
  transition: all 0.3s ease;
}

.quick-action-card:hover .action-arrow {
  transform: translateX(4px);
  color: #10b981;
}

.withdrawal-methods-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.method-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.method-card.selected {
  border-color: #10b981;
  background: #f0fdf4;
}

.method-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 12px;
  color: #10b981;
}

.method-info h6 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 2px;
}

.method-info p {
  font-size: 11px;
  color: #64748b;
  margin: 0;
}

.method-check {
  margin-left: auto;
}

.transactions-history-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.btn-export-history {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f1f5f9;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-export-history:hover {
  background: #e2e8f0;
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.transaction-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.transaction-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.transaction-details {
  flex: 1;
}

.transaction-header {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 6px;
}

.transaction-id {
  font-weight: 600;
  font-family: monospace;
  font-size: 13px;
  color: #1e293b;
}

.transaction-type {
  font-size: 11px;
  padding: 2px 8px;
  background: #e2e8f0;
  border-radius: 20px;
  color: #475569;
}

.transaction-meta {
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 11px;
  color: #64748b;
}

.transaction-amount {
  font-weight: 700;
  font-size: 16px;
}

.transaction-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
}

.view-all-wrapper {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #e8ecef;
}

.view-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-all-btn:hover {
  gap: 12px;
}

.modal-overlay-wallet {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container-wallet {
  background: white;
  border-radius: 24px;
  width: 90%;
  max-width: 500px;
  animation: modalSlideIn 0.3s ease;
}

.modal-header-wallet {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e8ecef;
}

.modal-header-wallet h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-close-wallet {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 24px;
}

.modal-body-wallet {
  padding: 24px;
}

.modal-info {
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.amount-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.amount-input-wrapper svg {
  position: absolute;
  left: 16px;
  color: #94a3b8;
}

.amount-input {
  width: 100%;
  padding: 14px 16px 14px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.amount-input:focus {
  outline: none;
  border-color: #10b981;
}

.method-selector {
  display: flex;
  gap: 12px;
}

.method-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.method-option.active {
  border-color: #10b981;
  background: #f0fdf4;
  color: #10b981;
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.btn-cancel {
  flex: 1;
  padding: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-submit {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .wallet-page {
    padding: 16px;
  }

  .balance-amount {
    font-size: 32px;
  }

  .balance-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-wallet-grid {
    grid-template-columns: 1fr;
  }

  .quick-actions-grid {
    grid-template-columns: 1fr;
  }

  .methods-grid {
    grid-template-columns: 1fr;
  }

  .transaction-item {
    flex-wrap: wrap;
  }

  .transaction-status {
    margin-left: 60px;
  }
}`}</style>
    </div>
  );
};

export default Wallet;